(() => {
  const MAX_PRODUCTS_PER_REQUEST = 100;
  const finePointerQuery = '(hover: hover) and (pointer: fine)';
  let refreshTimer = 0;

  const usesFinePointer = () => window.matchMedia(finePointerQuery).matches;

  const notePanel = (element) => element.querySelector?.('[data-fragrance-note-panel]');

  const closeNotePanel = (element) => {
    const panel = notePanel(element);
    if (panel && !panel.hidden) panel.hidden = true;
  };

  const closeDesktopNotePanelIfIdle = (element) => {
    const panel = notePanel(element);
    if (!panel || panel.hidden) return;
    if (panel.matches(':hover')) return;
    panel.hidden = true;
  };

  const syncSavedNoteState = (element) => {
    const noteButton = element.querySelector?.('[data-fragrance-action="note"]');
    if (!noteButton) return;
    const savedNote = typeof element.preferredNote === 'function'
      ? String(element.preferredNote() || '').trim()
      : '';
    noteButton.setAttribute('aria-pressed', String(Boolean(savedNote)));
  };

  const visibleGalleryMedia = (carousel) => {
    const mediaItems = [...carousel.querySelectorAll('.product-gallery__media:not([hidden])')];
    if (!mediaItems.length) return null;

    const carouselRect = carousel.getBoundingClientRect();
    const center = carouselRect.left + carouselRect.width / 2;
    return mediaItems.sort((left, right) => {
      const leftRect = left.getBoundingClientRect();
      const rightRect = right.getBoundingClientRect();
      const leftDistance = Math.abs(leftRect.left + leftRect.width / 2 - center);
      const rightDistance = Math.abs(rightRect.left + rightRect.width / 2 - center);
      return leftDistance - rightDistance;
    })[0];
  };

  const syncGalleryMediaState = (element) => {
    const imageList = element.closest('.product-gallery__image-list');
    const productGallery = imageList?.closest('product-gallery');
    const carousel = imageList?.querySelector('.product-gallery__carousel');
    if (!imageList || !productGallery || !carousel) return;

    const media = visibleGalleryMedia(carousel);
    const mediaType = String(media?.dataset.mediaType || 'image');
    imageList.dataset.activeMediaType = mediaType;
    productGallery.dataset.activeMediaType = mediaType;

    if (mediaType === 'video' || mediaType === 'external_video') {
      element.dataset.imageBright = 'light';
    }
  };

  const installElementEnhancements = (element) => {
    if (!element || element.__ornataInteractionEnhancementsInstalled) return;
    element.__ornataInteractionEnhancementsInstalled = true;

    const panel = notePanel(element);
    const clearPanelTimer = () => {
      window.clearTimeout(element.__ornataPanelCloseTimer);
      element.__ornataPanelCloseTimer = 0;
    };

    const schedulePanelClose = (delay = 140) => {
      if (!usesFinePointer()) return;
      const currentPanel = notePanel(element);
      if (!currentPanel || currentPanel.hidden) return;
      clearPanelTimer();
      element.__ornataPanelCloseTimer = window.setTimeout(
        () => closeDesktopNotePanelIfIdle(element),
        delay
      );
    };

    const handleElementEnter = () => clearPanelTimer();
    const handleElementLeave = () => schedulePanelClose();
    const handlePanelEnter = () => clearPanelTimer();
    const handlePanelLeave = () => schedulePanelClose();

    element.addEventListener('pointerenter', handleElementEnter);
    element.addEventListener('pointerleave', handleElementLeave);
    panel?.addEventListener('pointerenter', handlePanelEnter);
    panel?.addEventListener('pointerleave', handlePanelLeave);

    let carousel = null;
    let scheduleMediaSync = null;
    let galleryMediaObserver = null;

    if (element.classList.contains('ornata-fragrance-actions--gallery')) {
      const imageList = element.closest('.product-gallery__image-list');
      carousel = imageList?.querySelector('.product-gallery__carousel') || null;

      if (carousel) {
        scheduleMediaSync = () => {
          window.clearTimeout(element.__ornataGalleryMediaTimer);
          element.__ornataGalleryMediaTimer = window.setTimeout(() => syncGalleryMediaState(element), 80);
        };

        carousel.addEventListener('scroll', scheduleMediaSync, { passive: true });
        galleryMediaObserver = new MutationObserver(scheduleMediaSync);
        galleryMediaObserver.observe(carousel, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['hidden', 'class', 'src']
        });
        scheduleMediaSync();
      }
    }

    element.__ornataInteractionCleanup = () => {
      clearPanelTimer();
      window.clearTimeout(element.__ornataGalleryMediaTimer);
      element.__ornataGalleryMediaTimer = 0;
      element.removeEventListener('pointerenter', handleElementEnter);
      element.removeEventListener('pointerleave', handleElementLeave);
      panel?.removeEventListener('pointerenter', handlePanelEnter);
      panel?.removeEventListener('pointerleave', handlePanelLeave);
      if (carousel && scheduleMediaSync) carousel.removeEventListener('scroll', scheduleMediaSync);
      galleryMediaObserver?.disconnect();
      element.__ornataInteractionCleanup = null;
      element.__ornataInteractionEnhancementsInstalled = false;
    };
  };

  const installComponentEnhancements = () => {
    const component = customElements.get('ornata-fragrance-actions');
    const prototype = component?.prototype;
    if (!prototype || prototype.__ornataStorefrontEnhancementsInstalled) return;

    const render = prototype.render;
    if (typeof render === 'function') {
      prototype.render = function (...args) {
        const result = render.apply(this, args);
        syncSavedNoteState(this);
        return result;
      };
    }

    const openNotePanel = prototype.openNotePanel;
    if (typeof openNotePanel === 'function') {
      prototype.openNotePanel = function (...args) {
        const result = openNotePanel.apply(this, args);
        if (usesFinePointer()) {
          window.clearTimeout(this.__ornataPanelCloseTimer);
          this.__ornataPanelCloseTimer = window.setTimeout(
            () => closeDesktopNotePanelIfIdle(this),
            520
          );
        }
        return result;
      };
    }

    const connectedCallback = prototype.connectedCallback;
    prototype.connectedCallback = function (...args) {
      const result = typeof connectedCallback === 'function'
        ? connectedCallback.apply(this, args)
        : undefined;
      installElementEnhancements(this);
      syncSavedNoteState(this);
      return result;
    };

    const disconnectedCallback = prototype.disconnectedCallback;
    prototype.disconnectedCallback = function (...args) {
      this.__ornataInteractionCleanup?.();
      return typeof disconnectedCallback === 'function'
        ? disconnectedCallback.apply(this, args)
        : undefined;
    };

    prototype.__ornataStorefrontEnhancementsInstalled = true;
    document.querySelectorAll('ornata-fragrance-actions').forEach((element) => {
      installElementEnhancements(element);
      syncSavedNoteState(element);
    });
  };

  const post = async (element, productGids) => {
    const response = await fetch(element.dataset.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        action: 'public-member-fragrance-preferences',
        shopDomain: element.dataset.shopDomain,
        shopifyCustomerGid: element.dataset.customerGid,
        storefrontAccessToken: element.dataset.memberToken,
        productGids
      })
    });

    const body = await response.json().catch(() => null);
    if (!response.ok || body?.status !== 'ok') {
      throw new Error(body?.message || 'Voorkeuren konden niet worden geladen');
    }
    return body.data || {};
  };

  const chunks = (values, size) => {
    const result = [];
    for (let index = 0; index < values.length; index += size) {
      result.push(values.slice(index, index + size));
    }
    return result;
  };

  const waitForPrimaryHydration = (elements) => new Promise((resolve) => {
    if (elements.every((element) => element.dataset.ready === 'true')) {
      resolve();
      return;
    }

    const observer = new MutationObserver(() => {
      if (!elements.every((element) => element.dataset.ready === 'true')) return;
      observer.disconnect();
      window.clearTimeout(timeout);
      resolve();
    });

    elements.forEach((element) => observer.observe(element, {
      attributes: true,
      attributeFilter: ['data-ready']
    }));

    const timeout = window.setTimeout(() => {
      observer.disconnect();
      resolve();
    }, 5000);
  });

  const hydrateOverflow = async () => {
    const elements = [...document.querySelectorAll('ornata-fragrance-actions[data-auth-state="member"]')]
      .filter((element) => element.isConnected && typeof element.applyPreference === 'function');

    const groups = new Map();
    elements.forEach((element) => {
      const key = [element.dataset.endpoint, element.dataset.customerGid, element.dataset.memberToken].join('|');
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(element);
    });

    await Promise.all([...groups.values()].map(async (group) => {
      const productGids = [...new Set(group.map((element) => element.dataset.productGid).filter(Boolean))];
      if (productGids.length <= MAX_PRODUCTS_PER_REQUEST) return;

      const overflowGids = productGids.slice(MAX_PRODUCTS_PER_REQUEST);
      const overflowSet = new Set(overflowGids);
      const overflowElements = group.filter((element) => overflowSet.has(element.dataset.productGid));
      if (!overflowElements.length) return;

      await waitForPrimaryHydration(overflowElements);

      for (const productBatch of chunks(overflowGids, MAX_PRODUCTS_PER_REQUEST)) {
        try {
          const result = await post(group[0], productBatch);
          const byGid = new Map((Array.isArray(result.products) ? result.products : []).map((product) => [product.productGid, product]));
          overflowElements
            .filter((element) => productBatch.includes(element.dataset.productGid))
            .forEach((element) => element.applyPreference(byGid.get(element.dataset.productGid) || {}));
        } catch (error) {
          console.warn('Aanvullende member-geurvoorkeuren konden niet worden geladen.', error);
        }
      }
    }));
  };

  const scheduleHydration = () => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(hydrateOverflow, 90);
  };

  const closeMobileNoteOnOutsidePress = (event) => {
    if (usesFinePointer()) return;
    document.querySelectorAll('ornata-fragrance-actions').forEach((element) => {
      const panel = notePanel(element);
      if (!panel || panel.hidden || element.contains(event.target)) return;
      closeNotePanel(element);
    });
  };

  document.addEventListener('pointerdown', closeMobileNoteOnOutsidePress, true);

  if (customElements.get('ornata-fragrance-actions')) installComponentEnhancements();
  else customElements.whenDefined('ornata-fragrance-actions').then(installComponentEnhancements);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleHydration, { once: true });
  } else {
    scheduleHydration();
  }

  const observer = new MutationObserver((mutations) => {
    const addedFragranceActions = mutations.some((mutation) => [...mutation.addedNodes].some((node) =>
      node instanceof Element && (
        node.matches?.('ornata-fragrance-actions') ||
        node.querySelector?.('ornata-fragrance-actions')
      )
    ));
    if (addedFragranceActions) scheduleHydration();
  });

  const observe = () => {
    if (!document.body) return;
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.body) observe();
  else document.addEventListener('DOMContentLoaded', observe, { once: true });
})();
